import { Box, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import { Chart as ChartJS, registerables, type ChartTypeRegistry } from 'chart.js';
import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';

import getChartOptionsNew from 'utils/getChartOptionsNew';
import { CHART_COLORS } from 'projectConstants';
import {
  getPlanPageChartinnerWidth,
  getSelectedDriver,
  inputsPageShow,
  planPageShow,
  setPlanPageInnerWidth,
} from 'redux/PlanPageSlice';
import { NewDimItemData, NewTotalValues } from 'models/response/blockOutput.interface';
import {
  getBaseScenario,
  getCompareChartType,
  getCompareChartFilter,
  getCompareScenario,
  getValuesForSlider,
  setCompareChartType,
} from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import ChartTypeSwitcher from '../ChartTypeSwitcher';
import { usePlanPageRevampVariant } from '../../PlanPageUIRevamp/usePlanPageUiRevamp';
import {
  PLAN_CHART_SERIES_COLORS,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_TEXT,
} from '../../PlanPageUIRevamp/planPageRevampUi';

import CompareCustomRangeSlider from './CompareCustomRangeSlider';

import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

ChartJS.register(...registerables);
interface Properties {
  chartData: NewDimItemData[];
  chartLabels: string[];
  tabData: any;
  activeTabIndex: any;
  hideToolbar?: boolean;
  isLegend?: boolean;
  variant?: 'legacy' | 'revamp';
  exportRef?: React.MutableRefObject<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>;
  plotHeight?: string;
  fillPlotHeight?: boolean;
}

const CompareChart = ({
  chartData,
  chartLabels,
  hideToolbar = false,
  isLegend: isLegendProperty,
  variant = 'legacy',
  exportRef,
  plotHeight,
  fillPlotHeight = false,
}: Properties) => {
  const isRevampUi = usePlanPageRevampVariant(variant);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [isLegendInternal, setIsLegendInternal] = useState(true);
  const isLegend = isLegendProperty ?? isLegendInternal;
  const setIsLegend = setIsLegendInternal;
  const showInputPage = useSelector(inputsPageShow);
  const [chartKey, setChartKey] = useState(0);
  const dispatch: AppDispatch = useDispatch();
  const showPlanPage = useSelector(planPageShow);
  const chartFilter = useSelector(getCompareChartFilter);
  const currentDriver = useSelector(getSelectedDriver);
  const sliderLables = useSelector(getValuesForSlider);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isRevamp = isRevampUi;
  const containerReference = useRef<HTMLDivElement>(null);
  const innerWidth = useSelector(getPlanPageChartinnerWidth);
  const [chartNewData, setChartNewData] = useState<any>([]);

  useEffect(() => {
    const updateWindowDimensions = () => {
      const newWidth = window.innerWidth;
      setPageWidth(newWidth);
    };
    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  const chartTypeState = useSelector(getCompareChartType);
  const baseScenario = useSelector(getBaseScenario);
  const compareScenario = useSelector(getCompareScenario);

  useEffect(() => {
    setChartKey((previousKey) => previousKey + 1);
  }, [chartFilter, baseScenario, compareScenario?.id]);

  const toggleChartType = (newType: string) => {
    dispatch(setCompareChartType(newType));
  };

  const legendMargin = {
    id: 'legendMargin',
    beforeInit(chart: any) {
      const originalFit = chart.legend.fit;

      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)();
        this.height += 25;
      };
    },
  };

  const datasets1 = useMemo(() => {
    return chartData.map((entry: NewDimItemData) => {
      const data = entry?.value?.map((value: NewTotalValues) => value.value) || [];
      if (data.some((value) => value !== 0)) {
        while (data.length > 0 && data[data.length - 1] === 0) {
          data.pop();
        }
      }
      return {
        label: baseScenario?.name,
        data,
        backgroundColor: isRevamp ? PLAN_CHART_SERIES_COLORS[0] : CHART_COLORS[0],
        borderColor: isRevamp ? PLAN_CHART_SERIES_COLORS[0] : CHART_COLORS[0],
        pointRadius: 3,
      };
    });
  }, [baseScenario?.name, chartData, chartFilter, chartTypeState, isRevamp]);

  const datasets2 = useMemo(() => {
    return chartData.map((entry: NewDimItemData) => {
      const data = entry?.value?.map((value: NewTotalValues) => value.value2) || [];
      if (data.some((value) => value !== 0)) {
        while (data.length > 0 && data[data.length - 1] === 0) {
          data.pop();
        }
      }
      return {
        label: compareScenario?.name,
        data,
        backgroundColor:
          chartTypeState === 'line'
            ? isRevamp
              ? PLAN_CHART_SERIES_COLORS[0]
              : CHART_COLORS[0]
            : isRevamp
            ? PLAN_CHART_SERIES_COLORS[1]
            : CHART_COLORS[1],
        borderColor:
          chartTypeState === 'line'
            ? isRevamp
              ? PLAN_CHART_SERIES_COLORS[0]
              : CHART_COLORS[0]
            : isRevamp
            ? PLAN_CHART_SERIES_COLORS[1]
            : CHART_COLORS[1],
        borderDash: [isMobile ? 0.5 : 2, isMobile ? 2 : 2],
        pointRadius: 3,
      };
    });
  }, [chartData, chartTypeState, chartFilter, compareScenario?.name, isMobile, isRevamp]);

  useEffect(() => {
    setChartNewData([...datasets1, ...datasets2]);
    return () => {
      setChartNewData([]);
    };
  }, [compareScenario, datasets1, datasets2]);

  const data = {
    labels: chartLabels,
    datasets: chartNewData,
  };
  useLayoutEffect(() => {
    const updateContainerWidth = () => {
      setTimeout(() => {
        if (containerReference.current) {
          const containerWidth = containerReference.current.offsetWidth;
          const containerPadding =
            Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
            Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
          const widthWithoutPadding = containerWidth - containerPadding;
          if (widthWithoutPadding) dispatch(setPlanPageInnerWidth(widthWithoutPadding));
        }
      }, 200);
    };
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, [containerReference?.current, data, currentDriver, showInputPage, showPlanPage]);
  return hideToolbar ? (
    <Box
      display='flex'
      flexDirection='column'
      h={isRevamp && fillPlotHeight ? '100%' : 'auto'}
      minH={isRevamp && fillPlotHeight ? 0 : undefined}
      w='100%'
    >
      <Box
        flex={isRevamp && fillPlotHeight ? '1 1 0' : undefined}
        flexShrink={0}
        minH={isRevamp ? (fillPlotHeight ? 0 : plotHeight ?? PLAN_CHART_VIEW_HEIGHT) : '180px'}
        h={
          isRevamp ? (fillPlotHeight ? undefined : plotHeight ?? PLAN_CHART_VIEW_HEIGHT) : undefined
        }
        position='relative'
        w='100%'
        px={isRevamp ? 0 : '1%'}
        py={isRevamp ? 0 : '1%'}
        mb={isRevamp ? 0 : '1%'}
        sx={isRevamp ? undefined : { height: '200px' }}
      >
        <ReactChart
          key={chartKey}
          ref={(instance) => {
            if (exportRef) {
              exportRef.current = instance;
            }
          }}
          type={chartTypeState === 'line' ? 'line' : 'bar'}
          data={data}
          options={getChartOptionsNew({
            chartType: chartTypeState === 'line' ? 'line' : 'bar',
            isLegend: false,
            pageWidth: isRevamp ? undefined : pageWidth,
            aspectRatio: isRevamp ? 2.5 : isMobile ? 1.5 : 3,
            theme: isRevamp ? 'revamp' : 'legacy',
          })}
          plugins={[legendMargin]}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>

      {data?.labels && !isRevamp && (
        <Box flexShrink={0}>
          <CompareCustomRangeSlider labels={sliderLables} variant={variant} />
        </Box>
      )}

      {isRevamp && (data?.datasets?.length ?? 0) > 0 && (
        <Box
          flexShrink={0}
          borderTop='1px solid'
          borderColor={themeColors.border}
          bg={themeColors.surface}
          px={4}
          py={3}
          mx={`-${PLAN_HEADER_HORIZONTAL_PADDING}`}
        >
          <Flex
            ref={containerReference}
            flexWrap='wrap'
            gap={4}
            mx={`${PLAN_HEADER_HORIZONTAL_PADDING}`}
          >
            {data?.datasets?.map((_dataset: any, index: number) => (
              <Flex alignItems='center' gap='5px' key={index}>
                <Box
                  borderColor={_dataset.borderColor}
                  borderWidth={chartTypeState === 'line' ? '2px' : '0'}
                  bg={chartTypeState === 'line' ? 'transparent' : _dataset.backgroundColor}
                  h={chartTypeState === 'line' ? '2px' : '12px'}
                  w={chartTypeState === 'line' ? '15px' : '12px'}
                  borderRadius={chartTypeState === 'line' ? '0' : 'full'}
                  borderStyle={index === 0 ? 'solid' : 'dashed'}
                  flexShrink={0}
                />
                <Text {...PLAN_TEXT.labelSm} color={themeColors.textDark}>
                  {_dataset?.label}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {!isRevamp && isLegend && (
        <Flex
          ref={containerReference}
          display='flex'
          justifyContent='flex-start'
          alignItems='self-start'
          flexDirection='row'
          flexShrink={0}
          flexWrap='wrap'
        >
          {data?.datasets?.map((_dataset: any, index: number) => (
            <Flex alignItems='center' p='2px' ml={1} key={index}>
              <Box
                borderColor={chartTypeState === 'line' ? CHART_COLORS[0] : CHART_COLORS[index]}
                borderWidth='1px'
                bg={chartTypeState === 'line' ? 'none' : CHART_COLORS[index]}
                h={chartTypeState === 'line' ? '1px' : '10px'}
                w={chartTypeState === 'line' ? '15px' : '10px'}
                mr={chartTypeState === 'line' ? '0.5rem' : '0.1rem'}
                borderStyle={index === 0 ? 'solid' : 'dashed dashed'}
              />
              <Text
                color='black'
                fontSize='2xs'
                fontFamily='regular_roboto'
                textTransform='capitalize'
                fontWeight='500'
              >
                {_dataset?.label}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  ) : (
    <Flex flexDirection={'column'} padding={'0'}>
      <Flex
        style={{
          height: hideToolbar
            ? '220px'
            : isMobile
            ? innerWidth
              ? `${innerWidth / 1.5}px`
              : '35vh'
            : innerWidth
            ? `${innerWidth / 3}px`
            : '35vh',
          position: 'relative',
          marginBottom: '1%',
          padding: '1%',
          flexShrink: 0,
        }}
      >
        <ReactChart
          key={chartKey}
          type={chartTypeState === 'line' ? 'line' : 'bar'}
          data={data}
          options={getChartOptionsNew({
            chartType: chartTypeState === 'line' ? 'line' : 'bar',
            isLegend: false,
            pageWidth,
            aspectRatio: isMobile ? 1.5 : 3,
          })}
          plugins={[legendMargin]}
        />
      </Flex>

      <Flex
        ref={containerReference}
        display={'flex'}
        justifyContent={isMobile ? 'center' : 'space-between'}
        alignItems='self-start'
        flexDirection='row'
        pt={isMobile ? '0.7rem' : 0}
      >
        <Flex maxW={isMobile ? '100&' : '70%'} width={isMobile ? '100&' : '70%'} flexWrap={'wrap'}>
          {isLegend &&
            data?.datasets?.map((_data: any, index: number) => {
              return (
                <Flex alignItems={'center'} p={'2px'} ml={1}>
                  <Box
                    borderColor={chartTypeState === 'line' ? CHART_COLORS[0] : CHART_COLORS[index]}
                    borderWidth={'1px'}
                    bg={chartTypeState === 'line' ? 'none' : CHART_COLORS[index]}
                    h={chartTypeState === 'line' ? '1px' : '10px'}
                    w={chartTypeState === 'line' ? '15px' : '10px'}
                    mr={chartTypeState === 'line' ? '0.5rem' : '0.1rem'}
                    borderStyle={index === 0 ? 'solid' : 'dashed dashed'}
                  ></Box>
                  <Text
                    color='black'
                    fontSize={'2xs'}
                    fontFamily={'regular_roboto'}
                    textTransform={'capitalize'}
                    fontWeight={'500'}
                  >
                    {_data?.label}
                  </Text>
                </Flex>
              );
            })}
        </Flex>
        {!isMobile && !hideToolbar && (
          <Flex>
            <ChartTypeSwitcher
              viewMode={chartTypeState}
              onChange={toggleChartType}
              isLegend={isLegend}
              setIsLegend={setIsLegend}
            />
          </Flex>
        )}
      </Flex>
      {data?.labels && (
        <Box>
          <CompareCustomRangeSlider labels={sliderLables} />
        </Box>
      )}
      {isMobile && !hideToolbar && (
        <Flex
          ps='0.6rem'
          py='0.8rem'
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <ChartTypeSwitcher
            viewMode={chartTypeState}
            onChange={toggleChartType}
            isLegend={isLegend}
            setIsLegend={setIsLegend}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default CompareChart;
